import type { LocationQuery } from 'vue-router'

import { ImportWrapper } from '@/types/index'

import { PG_QUERY_END, PG_QUERY_START, SSR_QUERY } from './query-params'

const SSR_QUERY_VALUES: Set<string> = new Set(Object.values(SSR_QUERY))

type spyWebpackRequireEnsureCallback = (chunkId: string) => void
function spyWebpackRequireEnsure(spy: spyWebpackRequireEnsureCallback) {
    // save original fn
    const originalWebpackFn = __webpack_require__.e

    // spy the function
    __webpack_require__.e = (chunkId: string) => {
        // call the spy
        spy(chunkId)
        return originalWebpackFn(chunkId)
    }

    // return a callback to restore the original fn
    return () => {
        __webpack_require__.e = originalWebpackFn
    }
}

export const createRegisterImport: (registerCallback: spyWebpackRequireEnsureCallback) => ImportWrapper =
    (registerCallback) => (importCallback) => () => {
        const cleanup = spyWebpackRequireEnsure(registerCallback)
        const result = importCallback()
        cleanup()
        return result
    }

export function wrapQueryWithMarkers(query: LocationQuery): LocationQuery {
    const queryAsRecord = query as Record<string, string | string[]>
    if (queryAsRecord[PG_QUERY_START] || queryAsRecord[PG_QUERY_END]) {
        return query
    }
    return {
        [PG_QUERY_START]: '1',
        ...query,
        [PG_QUERY_END]: '1',
    } as LocationQuery
}

export function getStrippedUrlAndSearchParams(url: string): {
    strippedUrl: string
    strippedSearchParams: URLSearchParams
} {
    const queryIndex = url.indexOf('?')
    if (queryIndex < 0) {
        return { strippedUrl: url, strippedSearchParams: new URLSearchParams() }
    }

    const beforeQuery = url.slice(0, queryIndex)
    const afterQuery = url.slice(queryIndex + 1)

    const hashIndex = afterQuery.indexOf('#')
    const queryString = hashIndex >= 0 ? afterQuery.slice(0, hashIndex) : afterQuery
    const hash = hashIndex >= 0 ? afterQuery.slice(hashIndex) : ''

    const originalParams = new URLSearchParams(queryString)
    const strippedParams = new URLSearchParams()

    for (const key of [...SSR_QUERY_VALUES].sort()) {
        const values = originalParams.getAll(key)
        for (const value of values) {
            strippedParams.append(key, value)
        }
    }

    const newQuery = strippedParams.toString()
    return {
        strippedUrl: beforeQuery + (newQuery ? `?${newQuery}` : '') + hash,
        strippedSearchParams: strippedParams,
    }
}

function restoreQueryParamsInUrl(url: string, originalParams: URLSearchParams): string {
    const queryIndex = url.indexOf('?')
    if (queryIndex < 0) {
        return url
    }

    const beforeQuery = url.slice(0, queryIndex)
    const afterQuery = url.slice(queryIndex + 1)

    const hashIndex = afterQuery.indexOf('#')
    const queryString = hashIndex >= 0 ? afterQuery.slice(0, hashIndex) : afterQuery
    const hash = hashIndex >= 0 ? afterQuery.slice(hashIndex) : ''

    const renderedPairs: [string, string][] = []
    for (const [k, v] of new URLSearchParams(queryString)) {
        renderedPairs.push([k, v])
    }

    const startIdx = renderedPairs.findIndex(([k]) => k === PG_QUERY_START)
    const endIdx = renderedPairs.findIndex(([k]) => k === PG_QUERY_END)
    if (startIdx < 0 || endIdx < 0) {
        return url
    }

    const prefix = renderedPairs.slice(0, startIdx)
    const block = renderedPairs.slice(startIdx + 1, endIdx)
    const suffix = renderedPairs.slice(endIdx + 1)

    const originalValues = groupQueryValues([...originalParams])
    const prefixValues = groupQueryValues(prefix)
    const blockValues = groupQueryValues(block)
    const suffixValues = groupQueryValues(suffix)

    const emitted = new Set<string>()
    const result: [string, string][] = []

    // 1. Emit prefix: if key is also in original, use original value (spread overwrites)
    for (const [k] of prefix) {
        if (emitted.has(k)) {
            continue
        }
        emitted.add(k)
        appendQueryValues(result, k, originalValues.get(k) || prefixValues.get(k)!)
    }

    // 2. Replay original params in original order
    for (const [k] of originalParams) {
        if (k === PG_QUERY_START || k === PG_QUERY_END || emitted.has(k)) {
            continue
        }

        if (suffixValues.has(k)) {
            // Override via explicit-after: value changes, position stays at original
            emitted.add(k)
            appendQueryValues(result, k, suffixValues.get(k)!)
        } else if (blockValues.has(k)) {
            // Survived in block (possibly overridden by spread): use block value
            emitted.add(k)
            appendQueryValues(result, k, blockValues.get(k)!)
        } else if (SSR_QUERY_VALUES.has(k)) {
            // Allowlisted but absent: deliberately removed (e.g. { ...route.query, key: undefined })
        } else {
            // Unknown param filtered by SSR: restore from original
            emitted.add(k)
            appendQueryValues(result, k, originalValues.get(k)!)
        }
    }

    // 3. Append true suffix additions (keys not in original, added after spread)
    for (const [k] of suffix) {
        if (!emitted.has(k)) {
            emitted.add(k)
            appendQueryValues(result, k, suffixValues.get(k)!)
        }
    }

    const newParams = new URLSearchParams()
    for (const [k, v] of result) {
        newParams.append(k, v)
    }
    const newQuery = newParams.toString()
    return beforeQuery + (newQuery ? `?${newQuery}` : '') + hash
}

function groupQueryValues(pairs: Iterable<[string, string]>): Map<string, string[]> {
    const values = new Map<string, string[]>()
    for (const [key, value] of pairs) {
        const keyValues = values.get(key)
        if (keyValues) {
            keyValues.push(value)
        } else {
            values.set(key, [value])
        }
    }
    return values
}

function appendQueryValues(result: [string, string][], key: string, values: string[]) {
    for (const value of values) {
        result.push([key, value])
    }
}

export function restoreQueryParams(text: string, originalParams: URLSearchParams): string {
    if (!text.includes(PG_QUERY_START) && !text.includes(PG_QUERY_END)) {
        return text
    }

    const markerPattern = `${escapeRegex(PG_QUERY_START)}|${escapeRegex(PG_QUERY_END)}`
    const markerRegex = new RegExp(`(["'])((?:https?:)?[^"']*\\?[^"']*(?:${markerPattern})[^"']*)\\1`, 'g')

    if (markerRegex.test(text)) {
        markerRegex.lastIndex = 0
        return text.replace(markerRegex, (match, quote: string, capturedUrl: string) => {
            const wasEncoded = capturedUrl.includes('&amp;')
            const decoded = wasEncoded ? capturedUrl.replace(/&amp;/g, '&') : capturedUrl
            const result = restoreQueryParamsInUrl(decoded, originalParams)
            const finalResult = wasEncoded ? result.replace(/&/g, '&amp;') : result
            return `${quote}${finalResult}${quote}`
        })
    }

    return restoreQueryParamsInUrl(text, originalParams)
}

function escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

const fakeBase = 'http://localhost'

export function isSameRoute(routeA: string, routeB: string) {
    try {
        const parsedA = new URL(fakeBase + routeA)
        const parsedB = new URL(fakeBase + routeB)

        // strip SSR markers before comparing — they are infrastructure, not real params
        parsedA.searchParams.delete(PG_QUERY_START)
        parsedA.searchParams.delete(PG_QUERY_END)
        parsedB.searchParams.delete(PG_QUERY_START)
        parsedB.searchParams.delete(PG_QUERY_END)

        // sort search params to compare them
        parsedA.searchParams.sort()
        parsedB.searchParams.sort()

        return (
            parsedA.pathname.toLowerCase() === parsedB.pathname.toLowerCase() &&
            parsedA.searchParams.toString() === parsedB.searchParams.toString()
        )
    } catch (e) {
        throw new Error(`Invalid URL: ${e}`)
    }
}
